import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Bolt as BoostIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/**
 * Locsy Boost Control — referral credits (closed-loop).
 * Earn via referrals/leads; spend on own ads/listings. No cash-out.
 */
const BoostControl = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    effectiveEnabled: false,
    maxCheckoutPercent: 50,
    validityDays: 90,
    legalBlurb: '',
  });
  const [envFlag, setEnvFlag] = useState(false);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/boost/admin/control`, {
        headers: authHeaders(),
      });
      if (res.data?.success) {
        setSettings(res.data.data?.settings || settings);
        setRules(res.data.data?.rules || []);
        setEnvFlag(!!res.data.data?.envFlag);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Boost Control');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Boost Control - LOCSY';
    load();
  }, [load]);

  const updateRule = (ruleKey, patch) => {
    setRules((prev) =>
      prev.map((r) => (r.ruleKey === ruleKey ? { ...r, ...patch } : r)),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/boost/admin/control`,
        {
          settings: {
            enabled: settings.enabled,
            maxCheckoutPercent: Number(settings.maxCheckoutPercent),
            validityDays: Number(settings.validityDays),
            legalBlurb: settings.legalBlurb,
          },
          rules,
        },
        { headers: authHeaders() },
      );
      if (res.data?.success) {
        setSettings(res.data.data?.settings || settings);
        setRules(res.data.data?.rules?.length ? res.data.data.rules : rules);
        setEnvFlag(!!res.data.data?.envFlag);
        toast.success('Boost Control saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const summary = useMemo(() => {
    const enabled = rules.filter((r) => r.enabled).length;
    return { enabled, total: rules.length };
  }, [rules]);

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={800} gutterBottom>
              Boost Control · Locsy Boost
            </Typography>
            <Typography color="text.secondary" maxWidth={640}>
              Referral credits for growth. Users earn Boost from leads; they spend it on their
              own ads/listings (up to your %). Not cash. Non-transferable.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Safe MVP:</strong> closed-loop only (no UPI cash-out, no peer transfer). Requires
          server env <code>BOOST_ENABLED=true</code> <em>and</em> the toggle below. This is the
          old dummy Boost test. Live balances are in{' '}
          <RouterLink to="/content/coin-management">Wallet & Coin Management</RouterLink>.
        </Alert>

        {!envFlag && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Env flag <code>BOOST_ENABLED</code> is off. Saving rules is fine; earn/spend stay
            inactive until you set <code>BOOST_ENABLED=true</code> on the API server.
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
              <Paper sx={{ p: 2.5, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <BoostIcon color="primary" />
                  <Typography fontWeight={700}>Master switch</Typography>
                  {settings.effectiveEnabled ? (
                    <Chip size="small" color="success" label="Live" />
                  ) : (
                    <Chip size="small" label="Inactive" />
                  )}
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!settings.enabled}
                      onChange={(e) =>
                        setSettings((s) => ({ ...s, enabled: e.target.checked }))
                      }
                    />
                  }
                  label="Enable Locsy Boost (DB)"
                />
                <Typography variant="body2" color="text.secondary">
                  Effective = env flag && this toggle
                </Typography>
              </Paper>

              <Paper sx={{ p: 2.5, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <WalletIcon color="primary" />
                  <Typography fontWeight={700}>Checkout & expiry</Typography>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Max % of bill"
                    type="number"
                    size="small"
                    value={settings.maxCheckoutPercent}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        maxCheckoutPercent: Math.min(
                          100,
                          Math.max(0, Number(e.target.value) || 0),
                        ),
                      }))
                    }
                    inputProps={{ min: 0, max: 100 }}
                    helperText="Default 50"
                    sx={{ width: 140 }}
                  />
                  <TextField
                    label="Validity (days)"
                    type="number"
                    size="small"
                    value={settings.validityDays}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        validityDays: Math.min(
                          3650,
                          Math.max(1, Number(e.target.value) || 90),
                        ),
                      }))
                    }
                    inputProps={{ min: 1, max: 3650 }}
                    helperText="FIFO expiry"
                    sx={{ width: 140 }}
                  />
                </Stack>
              </Paper>

              <Paper sx={{ p: 2.5, flex: 1 }}>
                <Typography fontWeight={700} mb={1}>
                  Earn rules on
                </Typography>
                <Typography variant="h4">
                  {summary.enabled}/{summary.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active referral earn events
                </Typography>
              </Paper>
            </Stack>

            <Paper sx={{ p: 2.5, mb: 3 }}>
              <Typography fontWeight={700} mb={1}>
                Legal blurb (shown in apps)
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={settings.legalBlurb}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, legalBlurb: e.target.value }))
                }
              />
            </Paper>

            <Stack spacing={2}>
              {rules.map((rule) => (
                <Paper key={rule.ruleKey} sx={{ p: 2.5 }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ md: 'center' }}
                    justifyContent="space-between"
                  >
                    <Box flex={1}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Typography variant="h6" fontWeight={700}>
                          {rule.label}
                        </Typography>
                        <Chip size="small" label={rule.ruleKey} />
                        {!rule.enabled && <Chip size="small" label="Off" />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {rule.notes || 'Referral earn event'}
                      </Typography>
                    </Box>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!rule.enabled}
                          onChange={(e) =>
                            updateRule(rule.ruleKey, { enabled: e.target.checked })
                          }
                        />
                      }
                      label="Enabled"
                    />

                    <TextField
                      select
                      label="Mode"
                      size="small"
                      value={rule.rewardMode}
                      onChange={(e) =>
                        updateRule(rule.ruleKey, { rewardMode: e.target.value })
                      }
                      sx={{ width: 120 }}
                    >
                      <MenuItem value="percent">Percent</MenuItem>
                      <MenuItem value="flat">Flat ₹</MenuItem>
                    </TextField>

                    <TextField
                      label={rule.rewardMode === 'flat' ? 'Flat Boost ₹' : '% value'}
                      type="number"
                      size="small"
                      value={rule.rewardValue}
                      onChange={(e) =>
                        updateRule(rule.ruleKey, {
                          rewardValue: Math.max(0, Number(e.target.value) || 0),
                        })
                      }
                      inputProps={{ min: 0 }}
                      sx={{ width: 130 }}
                    />

                    <TextField
                      label="Max cap ₹"
                      type="number"
                      size="small"
                      value={rule.maxReward}
                      onChange={(e) =>
                        updateRule(rule.ruleKey, {
                          maxReward: Math.max(0, Number(e.target.value) || 0),
                        })
                      }
                      inputProps={{ min: 0 }}
                      helperText="0 = no cap"
                      sx={{ width: 120 }}
                    />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default BoostControl;
